import React, { useEffect, useRef, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { Alert, Box, Button, CircularProgress, LinearProgress, Skeleton, Typography } from '@mui/material'
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { createStripeOwnerOnboardingLink } from '../../apollo/mutations'
import { Card, PrimaryButton } from './ui'
import { color, focusRing, radius, type, usd } from './tokens'

const CREATE_LINK = gql`${createStripeOwnerOnboardingLink}`

const STEPS = [
  { key: 'business', title: 'Business details', body: 'Your business name, type and contact details.' },
  { key: 'bank', title: 'Bank account', body: 'Where your payouts are sent.' },
  { key: 'identity', title: 'Identity verification', body: 'Confirm the owner, so Stripe can pay you safely.' },
  { key: 'terms', title: 'Accept Stripe’s terms', body: 'Stripe can only pay you once the owner has agreed to them.' },
]

const STATUS_TEXT = { DONE: 'Complete', VERIFYING: 'Verifying', TODO: 'To do' }

function StepIcon({ status, current }) {
  if (status === 'DONE') return <CheckCircleIcon sx={{ color: color.success, fontSize: 24 }} />
  if (status === 'VERIFYING') return <HourglassTopOutlinedIcon sx={{ color: color.warning, fontSize: 22, m: '1px' }} />
  return <RadioButtonUncheckedIcon sx={{ color: current ? color.accent : color.faint, fontSize: 24 }} />
}

/**
 * State A: the account isn't onboarded yet. Four steps read from Stripe's
 * requirements, and one button that opens Stripe's onboarding form right in
 * this card. The owner fills in what's missing and accepts Stripe's terms
 * without leaving the dashboard. "Resume" once anything has been started.
 * Stripe's own site stays as a fallback.
 */
export default function SetupChecklist({
  restaurantId, steps, pendingAmount, pendingOrders, processingFeePercent, processingFeeFixed, autoStart = false,
  connect, connectError, formOpen, onOpenForm, onFormExit,
}) {
  const [createLink, { loading }] = useMutation(CREATE_LINK)
  const [error, setError] = useState(null)
  const [redirecting, setRedirecting] = useState(false)
  const [formError, setFormError] = useState(null)
  const autoStarted = useRef(false)

  const byKey = Object.fromEntries((steps || []).map((s) => [s.key, s.status]))
  const doneCount = STEPS.filter((s) => byKey[s.key] === 'DONE').length
  const started = STEPS.some((s) => byKey[s.key] && byKey[s.key] !== 'TODO')
  const currentKey = (STEPS.find((s) => byKey[s.key] !== 'DONE') || {}).key
  // Filled in from the partner application: only the terms are left.
  const onlyTerms = byKey.terms === 'TODO' && STEPS.every((s) => s.key === 'terms' || (byKey[s.key] && byKey[s.key] !== 'TODO'))

  const start = async () => {
    setError(null)
    try {
      const res = await createLink({ variables: { restaurantId } })
      const url = res?.data?.createStripeOwnerOnboardingLink?.url
      if (!url) throw new Error('Stripe did not return a setup link. Please try again.')
      setRedirecting(true)
      window.location.assign(url)
    } catch (e) {
      setError(e.message)
    }
  }

  // Stripe sends the owner back with ?setup=refresh when their link expired
  // mid-setup. Its guidance is to mint a new link and carry straight on —
  // once, so a failing link can't loop.
  useEffect(() => {
    if (autoStart && steps && !autoStarted.current) {
      autoStarted.current = true
      start()
    }
  }, [autoStart, steps])

  return (
    <Box sx={{ maxWidth: 680 }}>
      <Typography component="h1" sx={type.pageTitle}>Set up payouts</Typography>
      <Typography sx={{ ...type.body, color: color.muted, mt: 1, mb: { xs: 3, md: 4 }, maxWidth: 560, lineHeight: 1.6 }}>
        RushmyFood uses Stripe to pay you out securely.
        {processingFeePercent != null && (
          ` A card processing fee of ${Number(processingFeePercent)}% + ${usd(processingFeeFixed || 0)} comes out of your share on each card order.`
        )}
      </Typography>

      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: color.text }}>Your setup</Typography>
          <Typography sx={type.small}>
            {steps ? `${doneCount} of ${STEPS.length} complete` : ' '}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={steps ? (doneCount / STEPS.length) * 100 : 0}
          aria-label={`${doneCount} of ${STEPS.length} setup steps complete`}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: '#EBEEF1',
            mb: 2.5,
            '& .MuiLinearProgress-bar': { bgcolor: color.accent, borderRadius: 3 },
          }}
        />

        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {STEPS.map((s, i) => {
            const status = byKey[s.key] || 'TODO'
            const current = s.key === currentKey
            return (
              <Box
                component="li"
                key={s.key}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.75,
                  py: 1.75,
                  px: 1.5,
                  mx: -1.5,
                  borderRadius: '10px',
                  bgcolor: current && status === 'TODO' ? color.accentSoft : 'transparent',
                  borderTop: i === 0 ? 'none' : `1px solid ${current || STEPS[i - 1].key === currentKey ? 'transparent' : color.divider}`,
                  transition: 'background-color 180ms ease',
                }}
              >
                <Box sx={{ pt: '1px', flexShrink: 0 }}>
                  {steps ? <StepIcon status={status} current={current} /> : <Skeleton variant="circular" width={24} height={24} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: status === 'DONE' ? color.muted : color.text }}>
                    <Box component="span" sx={{ color: color.faint, fontWeight: 500, mr: 0.75 }}>{i + 1}.</Box>
                    {s.title}
                  </Typography>
                  <Typography sx={{ ...type.small, mt: 0.25 }}>{s.body}</Typography>
                </Box>
                {steps && (
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      flexShrink: 0,
                      color: status === 'DONE' ? color.success : status === 'VERIFYING' ? color.warningText : current ? color.accent : color.faint,
                    }}
                  >
                    {STATUS_TEXT[status]}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>

        {pendingAmount > 0 && (
          <Box sx={{ mt: 2, px: 2, py: 1.5, borderRadius: '10px', bgcolor: color.successSoft }}>
            <Typography sx={{ fontSize: 14, color: color.text }}>
              <strong>{usd(pendingAmount)}</strong> from {pendingOrders} {pendingOrders === 1 ? 'order is' : 'orders are'} waiting
              for you. It is paid out once setup is complete.
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {formOpen ? (
          <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${color.divider}` }}>
            {(connectError || formError) && <Alert severity="error" sx={{ mb: 2 }}>{connectError || formError}</Alert>}
            {!connect && !connectError && (
              <Box aria-busy="true">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} height={44} sx={{ bgcolor: '#EEF1F5', transform: 'none', mb: 1, borderRadius: radius.control }} />
                ))}
              </Box>
            )}
            {connect && (
              <ConnectComponentsProvider connectInstance={connect}>
                <ConnectAccountOnboarding
                  // Everything Stripe will ever ask for, now, so payouts aren't
                  // paused later for a missing detail.
                  collectionOptions={{ fields: 'eventually_due' }}
                  onExit={onFormExit}
                  onLoadError={() => setFormError('Stripe’s form didn’t load. Use the button below to continue on Stripe’s site.')}
                />
              </ConnectComponentsProvider>
            )}
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${color.divider}`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={type.small}>Having trouble with the form?</Typography>
              <Button
                onClick={start}
                disabled={loading || redirecting}
                startIcon={loading || redirecting ? <CircularProgress size={14} sx={{ color: color.accent }} /> : <OpenInNewIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', fontWeight: 600, color: color.accent, borderRadius: radius.control, ...focusRing }}
              >
                {'Continue on Stripe’s site'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <PrimaryButton
              onClick={onOpenForm}
              disabled={redirecting || !steps}
              endIcon={redirecting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ArrowForwardIcon />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {onlyTerms ? 'Review and accept terms' : started ? 'Resume setup' : 'Start setup'}
            </PrimaryButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LockOutlinedIcon sx={{ fontSize: 16, color: color.muted }} />
              <Typography sx={type.small}>
                {'Stripe’s secure form opens right here.'}
              </Typography>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  )
}
